import { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";

import useRequest from "../../utils/useRequest";

import { UserAuth } from "../../utils/AuthContext";

import editIcon from "../../assets/edit_icon.png";
import deleteIcon from "../../assets/delete_Icon.png";
import closeIcon from "../../assets/close_icon.png";
import saveIcon from "../../assets/save_icon.png";

import CustomButton from "../../components/CustomButton";
import Modal from "../../components/Modal";
import AreYouSureModal from "../../components/AreYouSureModal";
import ViewEditDiv from "../../components/ViewEditDiv";
import CustomInput from "../../components/CustomInput";
import Loader from "../../components/Loader";
import Dropdown from "../../components/Dropdown";
import { MEASURING_UNITS } from "./Inventory";

export default function ProductInfoModal({
  selectedRow,
  setSelectedRow,
  currencies,
  handleClose,
  getAllProducts,
}) {
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);

  const [showAreYouSure, setShowAreYouSure] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [tempProduct, setTempProduct] = useState(selectedRow);
  const [errorFields, setErrorFields] = useState({});

  const [selectedUnit, setSelectedUnit] = useState({
    title: t(selectedRow?.unitOfMeasure?.toLowerCase()),
    value: selectedRow?.unitOfMeasure,
  });

  const [purchasePriceCurrency, setPurchasePriceCurrency] = useState({
    title: t(selectedRow?.purchasePriceCurrency?.toLowerCase()),
    value: selectedRow?.purchasePriceCurrency,
  });

  const [sellingPriceCurrency, setSellingPriceCurrency] = useState({
    title: t(selectedRow?.sellingPriceCurrency?.toLowerCase()),
    value: selectedRow?.sellingPriceCurrency,
  });

  const {
    success: updatedSuccessfully,
    loading: updating,
    refetch: updateProduct,
  } = useRequest(
    `/editProduct/${selectedRow?.id}`,
    "PUT",
    {
      ...tempProduct,
      unitOfMeasure: selectedUnit?.value,
      purchasePriceCurrency: purchasePriceCurrency?.value,
      sellingPriceCurrency: sellingPriceCurrency?.value,
    },
    { organizationId: userData?.organizationId },
    false
  );

  const {
    success: deletedSuccessfully,
    loading: deleting,
    refetch: deleteProduct,
  } = useRequest(
    `/deleteProduct`,
    "DELETE",
    { id: selectedRow?.id },
    { organizationId: userData?.organizationId },
    false
  );

  useEffect(() => {
    if (updatedSuccessfully) {
      setEditMode(false);
      setSelectedRow(tempProduct);
      getAllProducts();
    }
  }, [updatedSuccessfully]);

  useEffect(() => {
    if (deletedSuccessfully) {
      setShowAreYouSure(false);
      setSelectedRow(null);
      getAllProducts();
    }
  }, [deletedSuccessfully]);

  const divClasses =
    "o-row o-row--gap-size_300 o-row--vertical-align_center u-margin_200 ";
  const pClasses = "u-margin_0 u-min-width-specific_100";

  const handleAttemptEdit = () => setEditMode(true);

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;

    setTempProduct((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setErrorFields({});
    setTempProduct(selectedRow);
  };

  const handleConfirmEdit = () => {
    updateProduct();
  };

  const handleDeleteProduct = async () => deleteProduct();

  return (
    <>
      <Modal
        title={t("productDetails")}
        onClose={handleClose}
        withDropdown={true}
        footer={
          <section className="o-row o-row--horizontal-align_end o-row--gap-size_300">
            {editMode ? (
              <>
                {updating ? <Loader /> : null}
                <CustomButton
                  title={t("cancel")}
                  onClick={handleCancelEdit}
                  variant="blank"
                  icon={closeIcon}
                  iconSize={14}
                />
                <CustomButton
                  title={t("confirm")}
                  onClick={handleConfirmEdit}
                  icon={saveIcon}
                  iconSize={16}
                  disabled={updating}
                />
              </>
            ) : (
              <>
                {deleting ? <Loader /> : null}

                <CustomButton
                  title={t("delete")}
                  variant="red"
                  icon={deleteIcon}
                  iconSize={20}
                  onClick={() => setShowAreYouSure(true)}
                  disabled={deleting}
                />

                <CustomButton
                  variant="blank"
                  title={t("edit")}
                  onClick={handleAttemptEdit}
                  icon={editIcon}
                  iconSize={18}
                  disabled={deleting}
                />
              </>
            )}
          </section>
        }
      >
        <div>
          <ViewEditDiv
            title="barcode"
            value={selectedRow?.barcode}
            divClasses={divClasses}
            pClasses={pClasses}
            editMode={editMode}
            InputElement={
              <CustomInput
                name="barcode"
                value={tempProduct?.barcode}
                onChange={handleEditInputChange}
                flex={true}
                errorText={errorFields?.barcode}
              />
            }
          />

          <ViewEditDiv
            title="name"
            value={selectedRow?.name}
            divClasses={divClasses}
            pClasses={pClasses}
            editMode={editMode}
            InputElement={
              <CustomInput
                name="name"
                value={tempProduct?.name}
                onChange={handleEditInputChange}
                flex={true}
                errorText={errorFields.name}
              />
            }
          />

          <ViewEditDiv
            title="purchasePrice"
            value={selectedRow?.purchasePrice}
            divClasses={divClasses}
            pClasses={pClasses}
            editMode={editMode}
            InputElement={
              <CustomInput
                name="purchasePrice"
                value={tempProduct?.purchasePrice}
                onChange={handleEditInputChange}
                flex={true}
                errorText={errorFields.purchasePrice}
              />
            }
          />

          <ViewEditDiv
            title="purchaseCurrency"
            value={t(purchasePriceCurrency?.title)}
            divClasses={divClasses}
            pClasses={pClasses}
            editMode={editMode}
            InputElement={
              <Dropdown
                options={currencies}
                selected={purchasePriceCurrency}
                setSelected={setPurchasePriceCurrency}
                flex
                errorText={errorFields.purchasePriceCurrency}
              />
            }
          />

          <ViewEditDiv
            title="price"
            value={selectedRow?.price}
            divClasses={divClasses}
            pClasses={pClasses}
            editMode={editMode}
            InputElement={
              <CustomInput
                name="price"
                value={tempProduct?.price}
                onChange={handleEditInputChange}
                flex={true}
                errorText={errorFields.price}
              />
            }
          />

          <ViewEditDiv
            title="sellingCurrency"
            value={t(sellingPriceCurrency?.title)}
            divClasses={divClasses}
            pClasses={pClasses}
            editMode={editMode}
            InputElement={
              <Dropdown
                options={currencies}
                selected={sellingPriceCurrency}
                setSelected={setSellingPriceCurrency}
                flex
                errorText={errorFields.sellingPriceCurrency}
              />
            }
          />

          <ViewEditDiv
            title="quantityInStock"
            value={selectedRow?.quantityInStock}
            divClasses={divClasses}
            pClasses={pClasses}
            editMode={editMode}
            InputElement={
              <CustomInput
                name="quantityInStock"
                value={tempProduct?.quantityInStock}
                onChange={handleEditInputChange}
                flex={true}
                errorText={errorFields.quantityInStock}
              />
            }
          />

          <ViewEditDiv
            title="measuringUnit"
            value={selectedUnit?.title}
            divClasses={divClasses}
            pClasses={pClasses}
            editMode={editMode}
            InputElement={
              <Dropdown
                options={MEASURING_UNITS}
                selected={selectedUnit}
                setSelected={setSelectedUnit}
                flex
              />
            }
          />
        </div>
      </Modal>
      {showAreYouSure ? (
        <AreYouSureModal
          onClose={() => setShowAreYouSure(false)}
          onConfirm={handleDeleteProduct}
          customMessage="areYouSureYouWantToDelete"
          closeTitle="noDontDelete"
          confirmTitle="yesDelete"
          loading={deleting}
        />
      ) : null}
    </>
  );
}
